import { Host, Component, h, State, Element, Prop, Watch } from '@stencil/core';

@Component({
  tag: 'dda-home-banner',
  styleUrls: ['home-banner.css'],
  shadow: false,
})
export class DdaHomeBanner {
  @Element() el: HTMLElement;

  /** Props */
  @Prop() autoplay: boolean = true;
  @Prop() interval: number = 3000;

  /** States */
  @State() currentSlide: number = 0;
  @State() slides: HTMLElement[] = [];
  @State() autoPlayActive: boolean = true;

  private autoPlayInterval: any;
  private pausedProgressOffset: number = 0;
  private animationFrameId: number | null = null;

  // componentDidLoad() {
  //   this.updateSlides();
  //   const slot = this.el.querySelector('slot');
  //   slot?.addEventListener('slotchange', () => this.updateSlides());

  //   this.autoPlayActive = this.autoplay && this.slides.length > 1;
  //   this.playVideoInCurrentSlide();

  //   if (this.autoPlayActive) {
  //     this.startAutoplay();
  //     this.animateProgress(); // Initial animation
  //   }
  // }

  componentDidLoad() {
  this.updateSlides();
  const slot = this.el.querySelector('slot');
  slot?.addEventListener('slotchange', () => this.updateSlides());

  this.autoPlayActive = this.autoplay && this.slides.length > 1;
  this.playVideoInCurrentSlide();

  // ✅ Wait for the DOM to fully render slides before animating
  if (this.autoPlayActive) {
    const tryStart = () => {
      const circle = this.el.querySelector('.circle-loader');
      if (circle) {
        this.startAutoplay();
        this.animateProgress();
      } else {
        requestAnimationFrame(tryStart);
      }
    };
    tryStart();
  }
}

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  componentDidRender() {
    this.slides.forEach((slide, index) => {
      slide.classList.toggle('active', index === this.currentSlide);
    });
  }

  updateSlides() {
    this.slides = Array.from(this.el.querySelectorAll('.dda-home-banner-slide'));
    if (this.slides.length > 0) {
      this.currentSlide = 0;
    }

    // Disable autoplay if there's only one slide
    if (this.slides.length <= 1) {
      this.autoPlayActive = false;
      this.stopAutoplay();
    }
  }

  startAutoplay() {
    this.stopAutoplay();

    if (this.autoPlayActive && this.slides.length > 1) {
      const slide = this.slides[this.currentSlide];
      const video = slide?.querySelector('video') as HTMLVideoElement;

      const setupTimer = (durationMs: number) => {
        this.autoPlayInterval = setInterval(() => {
          this.nextSlide();
        }, durationMs);
        this.animateProgress(1); // animate full ring
      };

      if (video) {
        if (video.readyState >= 1) {
          setupTimer(video.duration * 1000);
        } else {
          video.addEventListener(
            'loadedmetadata',
            () => {
              setupTimer(video.duration * 1000);
            },
            { once: true }
          );
        }
      } else {
        setupTimer(this.interval);
      }
    }
  }

  stopAutoplay() {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }
  }

  toggleAutoplay() {
    const circle = this.el.querySelector('.circle-loader') as SVGPathElement;
    const video = this.slides[this.currentSlide]?.querySelector('video') as HTMLVideoElement;

    this.autoPlayActive = !this.autoPlayActive;

    if (this.autoPlayActive) {
      this.startAutoplay();

      const offset = this.pausedProgressOffset;
      const totalLength = circle?.getTotalLength() || 1;
      const startRatio = offset / totalLength;

      if (video) video.play();
      this.animateProgress(startRatio);
    } else {
      this.stopAutoplay();

      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }

      if (circle) {
        const style = window.getComputedStyle(circle);
        const strokeDashoffset = parseFloat(style.strokeDashoffset || '0');
        this.pausedProgressOffset = strokeDashoffset;
      }

      if (video) video.pause();
    }
  }

  playVideoInCurrentSlide() {
    this.slides.forEach((slide, index) => {
      const video = slide.querySelector('video') as HTMLVideoElement;
      if (video) {
        if (index === this.currentSlide) {
          video.currentTime = 0;
          video.play();
        } else {
          video.pause();
        }
      }
    });
  }

  nextSlide() {
    this.currentSlide = (this.currentSlide + 1) % this.slides.length;
  }

  prevSlide() {
    this.currentSlide = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
  }

  setSlide(index: number) {
    this.currentSlide = index;
  }

  @Watch('currentSlide')
  onSlideChanged() {
    this.animateProgress();
    this.playVideoInCurrentSlide();
    this.startAutoplay(); // restart with correct duration
  }

  animateProgress(startRatio = 1) {
    const circle = this.el.querySelector('.circle-loader') as SVGPathElement;
    if (!circle) return;

    const length = circle.getTotalLength();
    const slide = this.slides[this.currentSlide];
    const video = slide?.querySelector('video') as HTMLVideoElement;
    const duration = (video && video.duration ? video.duration * 1000 : this.interval) * startRatio;
    const start = performance.now();
    this.pausedProgressOffset = length * startRatio;

    circle.style.strokeDasharray = `${length}`;
    circle.style.strokeDashoffset = `${this.pausedProgressOffset}`;
    circle.style.transition = 'none';

    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const newOffset = length * (1 - progress) * startRatio;
      circle.style.strokeDashoffset = `${newOffset}`;

      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(animate);
      }
    };

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.animationFrameId = requestAnimationFrame(animate);
  }

  resetProgress() {
    const circle = this.el.querySelector('.circle-loader') as SVGPathElement;
    if (circle) {
      const length = circle.getTotalLength();
      circle.style.transition = 'none';
      circle.style.strokeDashoffset = `${length}`;
      this.pausedProgressOffset = length;
    }
  }

  render() {
    const hasMultipleSlides = this.slides.length > 1;

    return (
      <Host>
        <div class="dda-home-banner" role="region" aria-label="Homepage banner with slides">
          <div class="dda-home-slides" role="list" aria-live="polite" aria-atomic="true">
            <slot></slot>
          </div>

          <div class="dda-home-banner-slider-nav">
            {hasMultipleSlides && (
              <div class="dda-home-banner-slider-nav-content">
                <button
                  type="button"
                  class="dda-home-banner-prev"
                  onClick={() => this.prevSlide()}
                >
                  <i class="material-icons" aria-hidden="true">chevron_left</i>
                  <span class="visually-hidden">Previous slide</span>
                </button>

                <button
                  type="button"
                  class="dda-home-banner-Pause"
                  onClick={() => this.toggleAutoplay()}
                  aria-pressed={this.autoPlayActive ? 'true' : 'false'}
                  aria-label={this.autoPlayActive ? 'Pause slideshow autoplay' : 'Play slideshow autoplay'}
                >
                  <span>
                    <svg
                      class="main-circle"
                      width="25"
                      height="26"
                      viewBox="0 0 25 26"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        class="circle-loader"
                        d="M8.61616 4.62474C9.35885 4.35949 10.4729 3.92017 12.146 3.92017C16.7619 3.92017 21.1489 7.55969 21.1489 12.9247C20.9849 18.8126 16.4271 21.9202 12.146 21.9202C7.78471 21.9202 3.16382 18.5036 3.16382 12.9601C3.16382 11.8748 3.2148 11.4931 3.48806 10.4602"
                        stroke="white"
                        stroke-width="2"
                        stroke-linecap="round"
                      />
                      <circle class="dott" cx="5.17456" cy="6.92017" r="1.44751" fill="white" />
                    </svg>
                    {this.autoPlayActive ? (
                      <svg
                        class="nav-pause-icon"
                        width="7"
                        height="7"
                        viewBox="0 0 7 7"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path d="M0.482666 0.548584H2.48267V6.54858H0.482666V0.548584Z" fill="white" />
                        <path d="M4.47974 0.548584H6.47974V6.54858H4.47974V0.548584Z" fill="white" />
                      </svg>
                    ) : (
                      <svg
                        class="nav-play-icon"
                        width="9"
                        height="9"
                        viewBox="0 0 9 9"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M7.9682 3.68255C8.63487 4.06745 8.63487 5.0297 7.9682 5.4146L2.31372 8.67922C1.64705 9.06412 0.813721 8.58299 0.813721 7.81319L0.813721 1.28396C0.813721 0.51416 1.64705 0.0330374 2.31372 0.417938L7.9682 3.68255Z"
                          fill="white"
                        />
                      </svg>
                    )}
                  </span>
                </button>

                <ul aria-label="Select slide">
                  {this.slides.map((_, index) => (
                    <li key={index}>
                      <button
                        type="button"
                        class={this.currentSlide === index ? 'dots active' : 'dots'}
                        onClick={() => this.setSlide(index)}
                        aria-label={`Go to slide ${index + 1}`}
                        aria-current={this.currentSlide === index ? 'true' : 'false'}
                      ></button>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  class="dda-home-banner-next"
                  onClick={() => this.nextSlide()}
                  aria-label="Next slide"
                >
                  <i class="material-icons" aria-hidden="true">chevron_right</i>
                  <span class="visually-hidden">Next slide</span>
                </button>
              </div>
            )}

            <div class="dda-mouse-scroll">
              <dda-scroll-icon
                scroll_icon_size="sm"
                scroll_icon_color="white"
                custom_class=""
                component_mode=""
                class="hydrated"
                aria-hidden="true"
              ></dda-scroll-icon>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
